import { z } from "zod";
import type { BuildProgress } from "../../domain/build/getProjectBuildGuide";
import {
	type FinishConfig,
	normalizeFinishes,
} from "../../domain/finishes/types";
import type { SiteConditions } from "../../domain/foundation/siteConditions";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import { normalizeWallHeightM } from "../../domain/geometry/buildingDimensions";
import {
	type IndoorRoomConfig,
	normalizeStoredIndoorRoom,
} from "../../domain/indoor/indoorRoom";
import { normalizeInteriorLayout } from "../../domain/interior/normalizeInteriorLayout";
import type { InteriorLayout } from "../../domain/interior/types";
import {
	type ClimateProfile,
	type LocationMode,
	type Market,
	normalizeClimateProfile,
} from "../../domain/location/types";
import { normalizeRoofConfig } from "../../domain/roof/roofConfig";
import {
	type SaunaRotationDeg,
	snapRotationDeg,
} from "../../domain/viewer/saunaRotation";
import {
	DEFAULT_TIME_OF_DAY,
	normalizeTimeOfDay,
	type TimeOfDayValue,
} from "../../domain/viewer/timeOfDay";
import { resolveTwoRoomConfig } from "../../engines/twoRoomSauna";
import type {
	DeckConfig,
	RoofConfig,
	SaunaDimensions,
	ThermalInputs,
	TwoRoomConfig,
	VisibilityFlags,
} from "../../engines/types";
import type { Design, DesignInsert, SaunaType } from "../../types/design";
import {
	DEFAULT_SITE_CONDITIONS,
	DEFAULT_THERMAL,
	DEFAULT_VIS,
} from "./constants";
import type { BuilderDraftState } from "./types";

// ─── Defensive shape guards for hydration ───────────────────
//
// fromDesignRow merges Design's optional nested objects with defaults
// via object spread. A non-object value at any of these slots would
// either crash the spread or silently corrupt the merged result. Even
// though designsRepo validates the DB row, hand-built Design objects
// (tests, future call sites) might still slip through — these guards
// keep the spread total: if the input isn't an object, fall back fully
// to the default.

const objectGuard = z.object({}).passthrough();
const arrayGuard = z.array(z.unknown());

function spreadable<T extends object>(value: unknown, fallback: T): T {
	const parsed = objectGuard.safeParse(value);
	return parsed.success ? (parsed.data as T) : fallback;
}

function objectOrNull<T extends object>(value: unknown): T | null {
	if (value === null || value === undefined) return null;
	const parsed = objectGuard.safeParse(value);
	return parsed.success ? (parsed.data as T) : null;
}

function arrayOr<T>(value: unknown, fallback: T[]): T[] {
	const parsed = arrayGuard.safeParse(value);
	return parsed.success ? (parsed.data as T[]) : fallback;
}

interface SaveShape {
	projectName: string;
	type: SaunaType;
	market: Market;
	locationMode: LocationMode;
	climateProfile: ClimateProfile;
	postalCode: string;
	dimensions: SaunaDimensions;
	partitionSplit: number;
	twoRoomConfig: TwoRoomConfig;
	timeOfDay: TimeOfDayValue;
	/** Optional so payloads built before this field existed still compile. */
	rotationDeg?: SaunaRotationDeg;
	wizardCapacityHint?: number | null;
	visibility: VisibilityFlags;
	openings: WallOpening[];
	deckConfig: DeckConfig | null;
	roofConfig: RoofConfig;
	buildProgress: BuildProgress;
	thermalInputs: ThermalInputs;
	siteConditions: SiteConditions;
	/** Optional so payloads built before this field existed still compile. */
	interiorLayout?: InteriorLayout | null;
	/** Optional for the same reason; absent saves the defaults. */
	finishes?: FinishConfig;
	/** Issue 155. Optional for the same reason; absent/null = the default. */
	wallHeightM?: number | null;
	/** Issues 174/175. Optional for the same reason; absent/null = the default. */
	indoorRoom?: IndoorRoomConfig | null;
}

export function toDesignPayload(input: SaveShape): DesignInsert {
	const twoRoomFields =
		input.type === "tvarum"
			? {
					entryWall: input.twoRoomConfig.entryWall,
					glazingStyle: input.twoRoomConfig.glazingStyle,
					saunaPosition: input.twoRoomConfig.saunaPosition,
					hasRelaxRoom: input.twoRoomConfig.hasRelaxRoom,
				}
			: {};

	return {
		name: input.projectName,
		type: input.type,
		market: input.market,
		locationMode: input.locationMode,
		postalCode: input.postalCode || undefined,
		climateProfile:
			input.locationMode === "manual_climate"
				? input.climateProfile
				: undefined,
		width: input.dimensions.width,
		length: input.dimensions.length,
		partitionSplit: input.partitionSplit,
		timeOfDay: normalizeTimeOfDay(input.timeOfDay),
		rotationDeg: snapRotationDeg(input.rotationDeg),
		wizardCapacityHint: input.wizardCapacityHint ?? null,
		...twoRoomFields,
		visibility: input.visibility,
		openings: input.openings,
		deckConfig: input.deckConfig,
		roofConfig: input.roofConfig,
		buildProgress: input.buildProgress,
		thermalInputs: input.thermalInputs,
		siteConditions: input.siteConditions,
		interiorLayout: input.interiorLayout ?? null,
		finishes: normalizeFinishes(input.finishes),
		// A barrel has no walls to raise; never persist a height it cannot use.
		wallHeightM:
			input.type === "tunna" ? null : normalizeWallHeightM(input.wallHeightM),
		// Only the indoor sauna stands in a room; never persist one it cannot use.
		indoorRoom:
			input.type === "inomhus"
				? normalizeStoredIndoorRoom(input.indoorRoom)
				: null,
	};
}

export function fromDesignRow(
	design: Design,
): Pick<
	BuilderDraftState,
	| "market"
	| "locationMode"
	| "climateProfile"
	| "dimensions"
	| "partitionSplit"
	| "twoRoomConfig"
	| "timeOfDay"
	| "rotationDeg"
	| "wizardCapacityHint"
	| "visibility"
	| "openings"
	| "deckConfig"
	| "roofConfig"
	| "buildProgress"
	| "thermalInputs"
	| "siteConditions"
	| "interiorLayout"
	| "finishes"
	| "wallHeightM"
	| "indoorRoom"
	| "projectName"
	| "postalCode"
> {
	return {
		market: design.market ?? "se",
		locationMode: design.locationMode ?? "postal_code",
		climateProfile: normalizeClimateProfile(design.climateProfile),
		dimensions: { width: design.width, length: design.length },
		partitionSplit:
			typeof design.partitionSplit === "number" ? design.partitionSplit : 0.5,
		twoRoomConfig: resolveTwoRoomConfig(
			design.type === "tvarum"
				? {
						entryWall: design.entryWall,
						glazingStyle: design.glazingStyle,
						saunaPosition: design.saunaPosition,
						hasRelaxRoom: design.hasRelaxRoom,
					}
				: {},
		),
		timeOfDay: normalizeTimeOfDay(design.timeOfDay ?? DEFAULT_TIME_OF_DAY),
		// Optional on Design: rows saved before the field existed hydrate to 0.
		rotationDeg: snapRotationDeg(design.rotationDeg),
		wizardCapacityHint: design.wizardCapacityHint ?? null,
		visibility: { ...DEFAULT_VIS, ...spreadable(design.visibility, {}) },
		openings: arrayOr<WallOpening>(design.openings, []),
		deckConfig: objectOrNull<DeckConfig>(design.deckConfig),
		roofConfig: normalizeRoofConfig(design.type, design.roofConfig),
		buildProgress: spreadable<BuildProgress>(
			design.buildProgress,
			{} as BuildProgress,
		),
		thermalInputs: {
			...DEFAULT_THERMAL,
			...spreadable<Partial<ThermalInputs>>(design.thermalInputs, {}),
		},
		siteConditions: {
			...DEFAULT_SITE_CONDITIONS,
			...spreadable(design.siteConditions, {}),
		},
		// Rows written before issue 130 have no column value at all; `null` is the
		// documented "no layout authored" state, so a legacy design falls back to
		// the generated default instead of hydrating a broken object.
		interiorLayout: normalizeInteriorLayout(design.interiorLayout),
		// Rows written before issue 134 have no column value; `normalizeFinishes`
		// resolves that to the defaults, which reproduce the look those rows were
		// designed against.
		finishes: normalizeFinishes(design.finishes),
		// Pre-issue-155 rows have no value: null builds the rule default.
		wallHeightM: normalizeWallHeightM(design.wallHeightM),
		indoorRoom:
			design.type === "inomhus"
				? normalizeStoredIndoorRoom(design.indoorRoom)
				: null,
		projectName: design.name,
		postalCode: design.postalCode ?? "",
	};
}

export function createFingerprint(input: {
	type: SaunaType;
	projectName: string;
	market: Market;
	locationMode: LocationMode;
	climateProfile: ClimateProfile;
	postalCode: string;
	dimensions: SaunaDimensions;
	partitionSplit: number;
	twoRoomConfig: TwoRoomConfig;
	timeOfDay: TimeOfDayValue;
	/** Optional so payloads built before this field existed still compile. */
	rotationDeg?: SaunaRotationDeg;
	wizardCapacityHint?: number | null;
	visibility: VisibilityFlags;
	openings: WallOpening[];
	deckConfig: DeckConfig | null;
	roofConfig: RoofConfig;
	buildProgress: BuildProgress;
	thermalInputs: ThermalInputs;
	siteConditions: SiteConditions;
	interiorLayout?: InteriorLayout | null;
	finishes?: FinishConfig;
	wallHeightM?: number | null;
	indoorRoom?: IndoorRoomConfig | null;
}): string {
	return JSON.stringify({
		projectName: input.projectName,
		type: input.type,
		market: input.market,
		locationMode: input.locationMode,
		climateProfile: input.climateProfile,
		postalCode: input.postalCode,
		w: input.dimensions.width,
		l: input.dimensions.length,
		ps: input.partitionSplit,
		twoRoomConfig: input.type === "tvarum" ? input.twoRoomConfig : undefined,
		timeOfDay: normalizeTimeOfDay(input.timeOfDay),
		rotationDeg: snapRotationDeg(input.rotationDeg),
		wizardCapacityHint: input.wizardCapacityHint ?? null,
		visibility: input.visibility,
		openings: input.openings,
		deckConfig: input.deckConfig,
		roofConfig: input.roofConfig,
		buildProgress: input.buildProgress,
		thermalInputs: input.thermalInputs,
		siteConditions: input.siteConditions,
		interiorLayout: input.interiorLayout ?? null,
		// In the fingerprint because a swatch click is a real, savable change:
		// leaving it out would make autosave skip a design whose 3D, BOM and
		// price all just moved.
		finishes: normalizeFinishes(input.finishes),
		// A height change moves the 3D, the BOM and the price: autosave must see it.
		wallHeightM: normalizeWallHeightM(input.wallHeightM),
		// A room edit moves the 3D, the BOM and the chimney: autosave must see it.
		indoorRoom:
			input.type === "inomhus"
				? normalizeStoredIndoorRoom(input.indoorRoom)
				: null,
	});
}
